import { useCallback, useEffect } from 'react';
import { Form, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useClosingPackageCommands } from './useClosingPackageCommands';
import { useClosingPackageTaskModalData } from './useClosingPackageTaskModalData';
import {
  buildEdoReceiptBody,
  getEdoReceiptCommentLabel,
  getEdoReceiptSubmitLabel,
  getEdoReceiptSuccessMessage,
  isEdoReceiptCommentRequired,
  validateConfirmEdoReceiptSubmit,
  type EdoReceiptResult,
} from '../lib/confirmEdoReceiptModalValidation';
import { mapClosingPackageApiError } from '../lib/errorMapping';

export type ConfirmEdoReceiptModalFormValues = {
  result: EdoReceiptResult;
  receivedAt: Dayjs;
  comment: string;
};

export type UseConfirmEdoReceiptModalOptions = {
  applicationId: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

export function useConfirmEdoReceiptModal({
  applicationId,
  open,
  onClose,
  onSuccess,
}: UseConfirmEdoReceiptModalOptions) {
  const [form] = Form.useForm<ConfirmEdoReceiptModalFormValues>();

  const { projection, contextView, contextLoading } = useClosingPackageTaskModalData({
    applicationId,
    open,
  });

  const { confirmEdoReceipt, states } = useClosingPackageCommands();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    form.setFieldsValue({
      result: 'RECEIVED',
      receivedAt: dayjs(),
      comment: '',
    });
  }, [form, open, applicationId]);

  const handleSubmit = useCallback(async () => {
    if (!projection) {
      message.error('Не удалось загрузить данные комплекта');
      return;
    }

    let values: ConfirmEdoReceiptModalFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validationIssue = validateConfirmEdoReceiptSubmit({
      result: values.result,
      eventAt: values.receivedAt,
      comment: values.comment,
    });
    if (validationIssue) {
      message.error(validationIssue.message);
      return;
    }

    try {
      await confirmEdoReceipt({
        applicationId,
        body: buildEdoReceiptBody({
          result: values.result,
          comment: values.comment,
          receivedAt: values.receivedAt.toISOString(),
        }),
      }).unwrap();

      message.success(getEdoReceiptSuccessMessage(values.result));
      await onSuccess?.();
      onClose();
    } catch (error) {
      message.error(mapClosingPackageApiError(error));
    }
  }, [applicationId, confirmEdoReceipt, form, onClose, onSuccess, projection]);

  const receiptResult = Form.useWatch('result', form) ?? 'RECEIVED';

  return {
    form,
    projection,
    contextView,
    contextLoading,
    receiptResult,
    commentRequired: isEdoReceiptCommentRequired(receiptResult),
    commentLabel: getEdoReceiptCommentLabel(receiptResult),
    submitting: states.confirmEdoReceipt.isLoading,
    submitLabel: getEdoReceiptSubmitLabel(receiptResult),
    handleSubmit,
  };
}
